import NotificationAddIcon from "@mui/icons-material/NotificationAdd";
import { Alert } from "@mui/material";

import { SkillSummary } from "@/shared/domain/skills";
import { Button } from "@/shared/foundation/primitives";

interface Props {
  summary: SkillSummary;
  /** The reader, for the author checks below. */
  userId: string;
  onSubscribe: () => void;
  isSubscribing: boolean;
}

/**
 * What this skill's accessibility tier means for the person reading it, and
 * what — if anything — they can do about it.
 *
 * A share link drops readers here with no other context: the header shows a
 * tier chip, and a chip alone doesn't say whether the reader may edit, why the
 * edit controls are missing, or whether subscribing would change that. The
 * three tiers each answer that differently, and the difference between the two
 * org tiers is the one worth being careful about:
 *
 * - `org-editable`: subscribing genuinely unlocks editing.
 * - `org`: subscribing files the skill under Personal so it can be used in the
 *   reader's own runs, and nothing more — the skill stays author-only to edit,
 *   however many people subscribe. Promising edit rights here would be a lie
 *   the UI never makes good on, so the two banners deliberately do not share
 *   their wording.
 *
 * Nothing is shown to anyone who can already act: the author of any tier, and
 * a subscriber of an `org-editable` skill.
 */
export function SkillAccessibilityBanner({
  summary,
  userId,
  onSubscribe,
  isSubscribing,
}: Props) {
  const isAuthor = summary.skill.created_by === userId;
  const isSubscribed = summary.subscription !== null;

  const subscribeAction = (
    <Button
      size="small"
      startIcon={<NotificationAddIcon fontSize="small" />}
      onClick={onSubscribe}
      loading={isSubscribing}
    >
      Subscribe
    </Button>
  );

  if (summary.skill.accessibility === "private") {
    // Only the author can see a private skill at all, so there is no reader
    // variant of this message to write.
    if (!isAuthor) {
      return null;
    }
    return (
      <Alert severity="info">
        Only you can see this skill. Change its accessibility to share it with
        your organization.
      </Alert>
    );
  }

  if (isAuthor) {
    // The author of an org-shared skill already has every control this banner
    // would explain the absence of.
    return null;
  }

  if (summary.skill.accessibility === "org") {
    return (
      <Alert
        severity="info"
        action={isSubscribed ? undefined : subscribeAction}
      >
        This skill is managed by your organization and can&apos;t be edited.
        {!isSubscribed &&
          " Subscribing files it under your Personal tab so you can use it in your own runs."}
      </Alert>
    );
  }

  if (isSubscribed) {
    // Already holds the edit rights the prompt below would be offering.
    return null;
  }
  return (
    <Alert severity="info" action={subscribeAction}>
      Anyone in this organization can edit this skill — but only after
      subscribing to it. Subscribing files it under your Personal tab and turns
      on the edit controls here.
    </Alert>
  );
}
